// data-fit LLC · DecisionFlow · Amendment Governance
// WizardStageSteps.jsx · API: v1.27 · S247 Phase 10
// OI-AMENDMENT-UI-REAPPROVAL-BADGE
// OI-AMENDMENT-UI-REAPPROVE-BUTTON
//
// Renders the per-stage wizard step list for comp_admin.
// Shows requires_reapproval amber badge on affected stages.
// Shows Reapprove button per stage when:
//   - step_status === 'requires_reapproval'
//   - AND a preview run has completed (previewRunCompleted prop)
//
// Props:
//   wizardSteps          array   — from GET /api/v1/wizard/session (steps[])
//   fiscalPeriodId       number
//   previewRunCompleted  boolean — enables Reapprove buttons
//   role                 string  — only render for comp_admin
//
// Visibility: comp_admin only. Returns null for all other roles.
// NN-03 / NN-04: no calculation or payout mutation here.

import React, { useState } from 'react'

const API_BASE = '/api/v1'

const STAGE_LABELS = {
  0:  'Initialise',
  1:  'Business Structure',
  2:  'Governance',
  3:  'Data Setup',
  4:  'Fiscal Calendar',
  5:  'Organisation',
  6:  'Sales Roles',
  7:  'Position Load',
  8:  'Position Detail',
  9:  'Plan Wizard',
  10: 'Plan Approval',
  11: 'Plan Assignment',
  12: 'Quota Load',
  13: 'Credit Config',
  14: 'Credit Load',
  15: 'Readiness',
  16: 'Calculation',
  17: 'Statements',
  18: 'Payroll',
  19: 'Certification',
}

const STATUS_DISPLAY = {
  not_started:         { label: 'Not started',      cls: 'bg-[rgb(var(--color-surface-muted))] text-[rgb(var(--color-text-muted))]' },
  in_progress:         { label: 'In progress',      cls: 'bg-[rgb(var(--color-primary))]/10 text-[rgb(var(--color-primary))]' },
  complete:            { label: 'Complete',          cls: 'bg-[rgb(var(--color-success))]/10 text-[rgb(var(--color-success))]' },
  error:               { label: 'Error',             cls: 'bg-[rgb(var(--color-danger))]/10 text-[rgb(var(--color-danger))]' },
  skipped:             { label: 'Skipped',           cls: 'bg-[rgb(var(--color-surface-muted))] text-[rgb(var(--color-text-muted))]' },
  requires_reapproval: { label: 'Reapproval needed', cls: 'bg-[rgb(var(--color-warning))]/15 text-[rgb(var(--color-warning))] border border-[rgb(var(--color-warning))]/40' },
}

async function postReapprove(stageId, fiscalPeriodId) {
  const res = await fetch(`${API_BASE}/wizard/stages/${stageId}/reapprove`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ fiscal_period_id: fiscalPeriodId }),
  })
  const payload = await res.json().catch(() => ({}))
  if (!res.ok) {
    const msg = payload?.error?.message || payload?.error?.recovery_action || `Error ${res.status}`
    throw new Error(msg)
  }
  return payload
}

export default function WizardStageSteps({ wizardSteps, fiscalPeriodId, previewRunCompleted, role }) {
  const [reapprovingId, setReapprovingId] = useState(null)
  const [reapproveErrors, setReapproveErrors] = useState({}) // { [stageId]: message }
  const [reapprovedIds, setReapprovedIds] = useState(new Set())

  // comp_admin only
  if (role !== 'comp_admin') return null
  if (!wizardSteps || wizardSteps.length === 0) return null

  const pendingStages = wizardSteps.filter((s) => s.step_status === 'requires_reapproval')
  if (pendingStages.length === 0) return null

  const handleReapprove = async (step) => {
    const stageId = step.stage_id ?? step.wizard_session_step_id
    setReapprovingId(stageId)
    setReapproveErrors((prev) => { const n = { ...prev }; delete n[stageId]; return n })
    try {
      await postReapprove(stageId, fiscalPeriodId)
      setReapprovedIds((prev) => new Set([...prev, stageId]))
    } catch (e) {
      setReapproveErrors((prev) => ({ ...prev, [stageId]: e.message }))
    } finally {
      setReapprovingId(null)
    }
  }

  return (
    <div className="rounded-2xl border border-[rgb(var(--color-warning))]/30 bg-[rgb(var(--color-warning))]/5 p-4 flex flex-col gap-3">
      {/* Header */}
      <div className="flex items-center gap-2">
        <span className="inline-flex h-5 w-5 items-center justify-center rounded-full bg-[rgb(var(--color-warning))]/20 text-tok-xs font-bold text-[rgb(var(--color-warning))]">
          {pendingStages.length}
        </span>
        <p className="text-tok-sm font-semibold text-[rgb(var(--color-warning))]">
          {pendingStages.length === 1 ? 'Stage requires reapproval' : 'Stages require reapproval'}
        </p>
        {!previewRunCompleted && (
          <span className="ml-auto text-tok-xs text-[rgb(var(--color-text-muted))]">
            Run a preview rerun to enable reapproval
          </span>
        )}
      </div>

      {/* Stage rows */}
      <div className="flex flex-col gap-2">
        {pendingStages.map((step) => {
          const stageId = step.stage_id ?? step.wizard_session_step_id
          const isReapproved = reapprovedIds.has(stageId)
          const isReapproving = reapprovingId === stageId
          const reapproveError = reapproveErrors[stageId]
          const stageLabel = step.stage_label ?? STAGE_LABELS[step.stage_id] ?? `Stage ${step.stage_id}`

          return (
            <div
              key={stageId}
              className={`flex flex-wrap items-center justify-between gap-2 rounded-xl border px-3 py-2.5 ${
                isReapproved
                  ? 'border-[rgb(var(--color-success))]/30 bg-[rgb(var(--color-success))]/5'
                  : 'border-[rgb(var(--color-warning))]/30 bg-[rgb(var(--color-surface))]'
              }`}
            >
              <div className="flex items-center gap-2 min-w-0">
                {/* Stage number badge */}
                <span className="shrink-0 inline-flex h-6 w-6 items-center justify-center rounded-full bg-[rgb(var(--color-warning))]/15 text-tok-xs font-semibold text-[rgb(var(--color-warning))]">
                  {step.stage_id ?? '?'}
                </span>
                <span className="text-tok-sm font-medium text-[rgb(var(--color-text))] truncate">
                  {stageLabel}
                </span>
                {/* requires_reapproval amber badge — OI-AMENDMENT-UI-REAPPROVAL-BADGE */}
                {!isReapproved && (
                  <span className={`shrink-0 inline-flex items-center rounded-full px-2 py-0.5 text-tok-xs font-semibold ${STATUS_DISPLAY.requires_reapproval.cls}`}>
                    ⚠ Reapproval needed
                  </span>
                )}
                {isReapproved && (
                  <span className="shrink-0 inline-flex items-center rounded-full bg-[rgb(var(--color-success))]/10 px-2 py-0.5 text-tok-xs font-semibold text-[rgb(var(--color-success))]">
                    ✓ Reapproved
                  </span>
                )}
              </div>

              <div className="flex flex-col items-end gap-1">
                {/* Reapprove button — OI-AMENDMENT-UI-REAPPROVE-BUTTON
                    Enabled only when preview run has completed AND not already reapproved */}
                {!isReapproved && (
                  <button
                    type="button"
                    onClick={() => handleReapprove(step)}
                    disabled={!previewRunCompleted || isReapproving || reapprovingId !== null}
                    title={
                      !previewRunCompleted
                        ? 'A preview rerun must complete before you can reapprove'
                        : 'Reapprove this stage'
                    }
                    className="rounded-lg bg-[rgb(var(--color-warning))] px-3 py-1.5 text-tok-xs font-semibold text-white hover:opacity-90 disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    {isReapproving ? 'Reapproving…' : 'Reapprove'}
                  </button>
                )}
                {reapproveError && (
                  <p className="text-tok-xs text-[rgb(var(--color-danger))]">{reapproveError}</p>
                )}
              </div>
            </div>
          )
        })}
      </div>

      {/* Governance note */}
      <p className="text-tok-xs text-[rgb(var(--color-text-muted))] border-t border-[rgb(var(--color-warning))]/20 pt-2">
        Each stage must be individually reapproved. The final run remains blocked until all stages above are cleared.
        Reapproval is only available after a preview rerun has completed.
      </p>
    </div>
  )
}
